import { Controller } from "@hotwired/stimulus"

// Field picker with search and grouped categories (like GHL)
export default class extends Controller {
  static targets = ["button", "dropdown", "search", "groups", "hiddenInput"]
  static values = {
    fields: Object,
    selected: String
  }

  connect() {
    this.isOpen = false
    this.expandedGroups = new Set() // Track multiple expanded groups for nested navigation
    this.updateButtonText()

    // Close dropdown when clicking outside
    this.outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) {
        this.close()
      }
    }
    document.addEventListener("click", this.outsideClickHandler)

    // Listen for other field pickers opening - close this one
    this.closeOthersHandler = (event) => {
      if (event.detail.controller !== this) {
        this.close()
      }
    }
    document.addEventListener("field-picker:opened", this.closeOthersHandler)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler)
    document.removeEventListener("field-picker:opened", this.closeOthersHandler)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Dispatch event to close other field pickers first
    document.dispatchEvent(new CustomEvent("field-picker:opened", { detail: { controller: this } }))

    this.isOpen = true
    this.dropdownTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    this.searchTarget.focus()
    this.renderGroups()

    // Lift the parent workflow-node above siblings so dropdown isn't clipped
    this.workflowNode = this.element.closest('.workflow-node')
    if (this.workflowNode) {
      this.workflowNode.style.zIndex = '50'
      this.workflowNode.style.position = 'relative'
    }
  }

  close() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
    this.expandedGroups.clear()

    // Reset the z-index when closing
    if (this.workflowNode) {
      this.workflowNode.style.zIndex = ''
      this.workflowNode.style.position = ''
    }
  }

  search() {
    const query = this.searchTarget.value.toLowerCase().trim()
    this.renderGroups(query)
  }

  // Flatten the nested structure to get all field key/label pairs
  flattenFields() {
    const fields = this.fieldsValue
    const result = {}

    for (const [modelOrGroup, subGroups] of Object.entries(fields)) {
      // Check if this is a nested structure (model -> groups -> fields)
      // or flat structure (group -> fields)
      const firstValue = Object.values(subGroups)[0]
      if (typeof firstValue === 'object' && firstValue !== null) {
        // Nested: model -> groups -> fields
        for (const [groupName, groupFields] of Object.entries(subGroups)) {
          for (const [fieldKey, fieldLabel] of Object.entries(groupFields)) {
            result[fieldKey] = { label: fieldLabel, model: modelOrGroup, group: groupName }
          }
        }
      } else {
        // Flat: group -> fields (string values)
        for (const [fieldKey, fieldLabel] of Object.entries(subGroups)) {
          result[fieldKey] = { label: fieldLabel, model: null, group: modelOrGroup }
        }
      }
    }
    return result
  }

  renderGroups(searchQuery = "") {
    const fields = this.fieldsValue
    const flatFields = this.flattenFields()
    let html = ""

    if (searchQuery) {
      // Show flat list of matching fields
      html = '<div class="py-1">'
      for (const [fieldKey, fieldInfo] of Object.entries(flatFields)) {
        const fieldLabel = fieldInfo.label
        const context = fieldInfo.model ? `${fieldInfo.model} › ${fieldInfo.group}` : fieldInfo.group
        if (fieldLabel.toLowerCase().includes(searchQuery) || fieldKey.toLowerCase().includes(searchQuery)) {
          const isSelected = fieldKey === this.selectedValue
          html += `
            <button type="button"
                    data-action="click->field-picker#selectField"
                    data-field-key="${fieldKey}"
                    data-field-label="${fieldLabel}"
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}">
              <span>${fieldLabel}</span>
              <span class="text-xs text-gray-400">${context}</span>
            </button>
          `
        }
      }
      html += '</div>'
    } else {
      // Show grouped view with expandable categories
      // Support both nested (Model > Group > Fields) and flat (Group > Fields) structures
      html = '<div class="py-1">'

      for (const [topLevel, subContent] of Object.entries(fields)) {
        const firstValue = Object.values(subContent)[0]
        const isNested = typeof firstValue === 'object' && firstValue !== null

        if (isNested) {
          // Model-level header (e.g., "Company", "SearchResult", "Contact")
          const isModelExpanded = this.expandedGroups.has(topLevel)
          let totalFields = 0
          for (const groupFields of Object.values(subContent)) {
            totalFields += Object.keys(groupFields).length
          }

          html += `
            <button type="button"
                    data-action="click->field-picker#toggleGroup"
                    data-group-name="${topLevel}"
                    class="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-900 bg-gray-100 hover:bg-gray-200 flex items-center justify-between border-b border-gray-200">
              <span>${topLevel}</span>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500">${totalFields} fields</span>
                <svg class="w-4 h-4 text-gray-400 transition-transform ${isModelExpanded ? 'rotate-90' : ''}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            </button>
          `

          if (isModelExpanded) {
            // Show sub-groups
            for (const [groupName, groupFields] of Object.entries(subContent)) {
              const subGroupKey = `${topLevel}:${groupName}`
              const isSubGroupExpanded = this.expandedGroups.has(subGroupKey)
              const fieldCount = Object.keys(groupFields).length

              html += `
                <button type="button"
                        data-action="click->field-picker#toggleGroup"
                        data-group-name="${subGroupKey}"
                        class="w-full text-left px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-between border-b border-gray-50">
                  <span>${groupName}</span>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-400">${fieldCount}</span>
                    <svg class="w-3 h-3 text-gray-400 transition-transform ${isSubGroupExpanded ? 'rotate-90' : ''}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                </button>
              `

              if (isSubGroupExpanded) {
                html += '<div class="bg-gray-50 py-1">'
                for (const [fieldKey, fieldLabel] of Object.entries(groupFields)) {
                  const isSelected = fieldKey === this.selectedValue
                  html += `
                    <button type="button"
                            data-action="click->field-picker#selectField"
                            data-field-key="${fieldKey}"
                            data-field-label="${fieldLabel}"
                            class="w-full text-left px-8 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}">
                      ${isSelected ? '<svg class="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>' : '<span class="w-4"></span>'}
                      <span>${fieldLabel}</span>
                    </button>
                  `
                }
                html += '</div>'
              }
            }
          }
        } else {
          // Flat group structure (for backward compatibility)
          const isExpanded = this.expandedGroups.has(topLevel)
          const fieldCount = Object.keys(subContent).length

          html += `
            <button type="button"
                    data-action="click->field-picker#toggleGroup"
                    data-group-name="${topLevel}"
                    class="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 flex items-center justify-between border-b border-gray-100">
              <span>${topLevel}</span>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-400">${fieldCount}</span>
                <svg class="w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            </button>
          `

          if (isExpanded) {
            html += '<div class="bg-gray-50 py-1">'
            for (const [fieldKey, fieldLabel] of Object.entries(subContent)) {
              const isSelected = fieldKey === this.selectedValue
              html += `
                <button type="button"
                        data-action="click->field-picker#selectField"
                        data-field-key="${fieldKey}"
                        data-field-label="${fieldLabel}"
                        class="w-full text-left px-6 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}">
                  ${isSelected ? '<svg class="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>' : '<span class="w-4"></span>'}
                  <span>${fieldLabel}</span>
                </button>
              `
            }
            html += '</div>'
          }
        }
      }
      html += '</div>'
    }

    this.groupsTarget.innerHTML = html
  }

  toggleGroup(event) {
    event.preventDefault()
    event.stopPropagation()

    const groupName = event.currentTarget.dataset.groupName

    if (this.expandedGroups.has(groupName)) {
      this.expandedGroups.delete(groupName)
    } else {
      this.expandedGroups.add(groupName)
    }

    this.renderGroups()
  }

  selectField(event) {
    event.preventDefault()
    event.stopPropagation()

    const fieldKey = event.currentTarget.dataset.fieldKey
    const fieldLabel = event.currentTarget.dataset.fieldLabel

    this.selectedValue = fieldKey
    this.hiddenInputTarget.value = fieldKey
    this.updateButtonText()
    this.close()

    // Dispatch change event for any listeners
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateButtonText() {
    const flatFields = this.flattenFields()
    let label = "Select field..."

    if (this.selectedValue && flatFields[this.selectedValue]) {
      label = flatFields[this.selectedValue].label
    }

    this.buttonTarget.querySelector("span").textContent = label
  }
}
